import { useState, useCallback } from 'react';
import type { ArrowType } from '../components/ArrowSection';
import type { TemplateMode } from '../components/BatchUpload';
import { generate } from '@alchemiesolns/llm-api';
import { systemPromptFor, resolveContextReference } from '../utils/prompts';
import { fromConfigString } from '@alchemiesolns/workspace-serialization';
import { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';
import { extractWorkspaceFeatures } from '@alchemiesolns/feature-extractor-compat';
import {
  buildWorkspacePackage,
  buildReactionSchemePackage,
  type ProductSpec,
  type ReactantSpec,
  type WorkspaceSpec,
} from '@alchemiesolns/svg-interactive';

interface UseAltTextGenerationProps {
  templateMode: TemplateMode;
  reactantConfig: string;
  productConfig: string;
  /** Author-entered overlay text replacing the reactant structure (PIPH-247). */
  reactantOverlayText: string;
  /** Author-entered overlay text replacing the product structure (PIPH-247). */
  productOverlayText: string;
  topText: string;
  bottomText: string;
  arrowType: ArrowType;
  /** Optional literal override (dev mode). Empty string = use templateMode-based reference. */
  systemPrompt: string;
  /** Context flavor id from CONTEXT_PROMPT_OPTIONS (e.g., 'none', 'resonance'). */
  selectedContextOption: string;
  altText: string;
  setAltText: (text: string) => void;
}

export function useAltTextGeneration({
  templateMode,
  reactantConfig,
  productConfig,
  reactantOverlayText,
  productOverlayText,
  topText,
  bottomText,
  arrowType: _arrowType,
  systemPrompt,
  selectedContextOption,
  altText,
  setAltText,
}: UseAltTextGenerationProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [altTextCopied, setAltTextCopied] = useState(false);

  const generateAltText = useCallback(async (): Promise<boolean> => {
    setIsGenerating(true);
    try {
      let payload: string;

      // Overlay text wins over the structure: the UI invariant is that overlay
      // text is non-empty iff the structure was cleared (PIPH-247).
      const buildWorkspaceFromConfig = (config: string) => {
        const workspace = config ? fromConfigString(config) : new MolecularWorkspace();
        return { workspace, featureResult: extractWorkspaceFeatures(workspace) };
      };

      if (templateMode === 'molecule') {
        const workspaceSpec: WorkspaceSpec = reactantOverlayText
          ? { type: 'rawText', text: reactantOverlayText }
          : { type: 'workspace', ...buildWorkspaceFromConfig(reactantConfig) };
        payload = buildWorkspacePackage(workspaceSpec);
      } else {
        const reactantSpec: ReactantSpec = reactantOverlayText
          ? { type: 'rawText', text: reactantOverlayText }
          : { type: 'workspace', ...buildWorkspaceFromConfig(reactantConfig) };

        let productSpec: ProductSpec;
        if (productOverlayText) {
          productSpec = { type: 'rawText', text: productOverlayText };
        } else if (productConfig) {
          productSpec = { type: 'workspace', ...buildWorkspaceFromConfig(productConfig) };
        } else {
          productSpec = { type: 'unknown' };
        }

        payload = buildReactionSchemePackage(
          reactantSpec,
          { aboveText: topText, belowText: bottomText },
          productSpec
        );
      }

      const isMolecule = templateMode === 'molecule';
      const response = await generate({
        appName: 'piph-author',
        // Dev override (literal text) wins over the registered reference name
        systemPrompt: systemPrompt || systemPromptFor(templateMode),
        contextPrompt: isMolecule ? undefined : resolveContextReference(selectedContextOption),
        model: 'claude-sonnet-4-6',
        data: payload,
      });

      console.log("api message", response)
      const json = response.json as { workspace?: string, altText?: string}  | null;
      setAltText(json?.workspace ?? json?.altText ?? response.raw);
      return true;
    } catch (error) {
      console.error('=== Alt Text API Error ===');
      console.error('Error:', error);
      setAltText(`Error: ${error}`);
      return false;
    } finally {
      setIsGenerating(false);
    }
  }, [
    templateMode,
    reactantConfig,
    productConfig,
    reactantOverlayText,
    productOverlayText,
    topText,
    bottomText,
    systemPrompt,
    selectedContextOption,
    setAltText,
  ]);

  const copyAltText = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(altText);
      setAltTextCopied(true);
      setTimeout(() => setAltTextCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = altText;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setAltTextCopied(true);
      setTimeout(() => setAltTextCopied(false), 2000);
    }
  }, [altText]);

  return {
    isGenerating,
    altTextCopied,
    generateAltText,
    copyAltText,
  };
}